import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { SlotDay } from '../api/types';
import type {
  Agenda, AppointmentCard, ChildCard, Colleague, Conflict, Page, Referral, Schedule,
  Series, SeriesPreview, StaffChild, WorkRule,
} from './types';

/**
 * Запросы рабочего места. `specialistId` сервер подставляет из initData — в теле
 * его нет ни в одном запросе: иначе специалист правил бы чужое расписание.
 */

export const staffKeys = {
  agenda: (date: string) => ['staff', 'agenda', date] as const,
  week: (from: string, to: string) => ['staff', 'week', from, to] as const,
  appointment: (id: number) => ['staff', 'appointment', id] as const,
  schedule: ['staff', 'schedule'] as const,
  children: ['staff', 'children'] as const,
  child: (id: number) => ['staff', 'child', id] as const,
  referrals: ['staff', 'referrals'] as const,
};

export function useAgenda(date: string) {
  return useQuery({
    queryKey: staffKeys.agenda(date),
    queryFn: () => api.get<Agenda>('/api/staff/agenda', { date }),
  });
}

/** Неделя целиком — экран «Календарь». Сервер отдаёт диапазон одним запросом. */
export function useStaffWeek(from: string, to: string) {
  return useQuery({
    queryKey: staffKeys.week(from, to),
    queryFn: () => api.get<Agenda>('/api/staff/agenda', { from, to }),
  });
}

export function useAppointmentCard(id: number | null) {
  return useQuery({
    queryKey: staffKeys.appointment(id ?? 0),
    queryFn: () => api.get<AppointmentCard>(`/api/staff/appointments/${id}`),
    enabled: id !== null,
  });
}

export function useSchedule() {
  return useQuery({
    queryKey: staffKeys.schedule,
    queryFn: () => api.get<Schedule>('/api/staff/schedule'),
  });
}

export function useStaffChildren(limit = 50) {
  return useInfiniteQuery({
    queryKey: [...staffKeys.children, limit],
    queryFn: ({ pageParam }) => api.get<Page<StaffChild>>('/api/staff/children', { limit, offset: pageParam }),
    initialPageParam: 0,
    // Следующая страница есть, пока показано меньше, чем всего.
    getNextPageParam: (last) => (last.offset + last.items.length < last.total ? last.offset + last.limit : undefined),
  });
}

/** Коллеги — чтобы направить к ним ребёнка. */
export function useColleagues() {
  return useQuery({
    queryKey: ['staff', 'colleagues'],
    queryFn: () => api.get<Colleague[]>('/api/staff/specialists'),
    staleTime: 5 * 60_000,
  });
}

/** Направление к коллеге: закрепление создаёт админ, специалист только просит. */
export function useCreateReferral() {
  const refresh = useRefreshDay();
  return useMutation({
    mutationFn: (b: { childId: number; directionId?: number | null; preferredSpecialistId?: number | null; note?: string }) =>
      api.post<unknown>('/api/staff/referrals', b),
    onSuccess: refresh,
  });
}

export function useStaffChild(id: number | null) {
  return useQuery({
    queryKey: staffKeys.child(id ?? 0),
    queryFn: () => api.get<ChildCard>(`/api/staff/children/${id}`),
    enabled: id !== null,
  });
}

export function useStaffReferrals() {
  return useQuery({
    queryKey: staffKeys.referrals,
    queryFn: () => api.get<Referral[]>('/api/staff/referrals'),
  });
}

/**
 * Свободные окна для переноса приёма. `childId` передаём: сервер вычтет время,
 * когда ребёнок занят у коллег, — иначе специалист выбирал бы слот, который
 * всё равно отказал бы кодом CHILD_BUSY.
 */
/**
 * Свободные окна. `exceptAppointmentId` — переносимая запись: сервер вычитает её из
 * занятости, иначе она вместе с буфером загораживает и своё время, и соседние окна.
 */
export function useStaffSlots(params: {
  specialistId: number | null;
  serviceId: number | null;
  childId: number | null;
  from?: string;
  to?: string;
  exceptAppointmentId?: number;
}) {
  const { specialistId, serviceId, childId, from, to, exceptAppointmentId } = params;
  return useQuery({
    queryKey: ['slots', 'staff', specialistId, serviceId, childId, from ?? '', to ?? '', exceptAppointmentId ?? 0],
    queryFn: () => api.get<SlotDay[]>('/api/slots', {
      specialistId: specialistId!, serviceId: serviceId!, childId: childId ?? undefined,
      from, to, exceptAppointmentId,
    }),
    enabled: specialistId !== null && serviceId !== null,
    staleTime: 15_000,
  });
}

/** Разослать родителям изменения недели одним письмом на каждого. */
export function useAnnounceWeek() {
  const refresh = useRefreshDay();
  return useMutation({
    mutationFn: (v: { from: string; to: string }) =>
      api.post<{ sent: number; changes: number }>('/api/staff/week/announce', v),
    onSuccess: refresh,
  });
}

/** Занятие, поставленное самим специалистом в свободное окно. */
export function useCreateAppointment() {
  const refresh = useRefreshDay();
  return useMutation({
    mutationFn: (v: { childId: number; serviceId: number; startsAt: number }) =>
      api.post<unknown>('/api/staff/appointments', v),
    onSuccess: refresh,
  });
}

function useRefreshDay() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['staff'] });
    void qc.invalidateQueries({ queryKey: ['slots'] });
  };
}

export function useAppointmentAction() {
  const refresh = useRefreshDay();
  return useMutation({
    mutationFn: (v:
      | { id: number; action: 'complete' | 'mark-paid' }
      | { id: number; action: 'cancel'; reason?: string; refund: boolean }
      | { id: number; action: 'no-show'; payment: 'keep' | 'refund' | 'carry' }
      | { id: number; action: 'reschedule'; startsAt: number }) => {
      const { id, action, ...body } = v as { id: number; action: string } & Record<string, unknown>;
      return api.post<unknown>(`/api/staff/appointments/${id}/${action}`, body);
    },
    onSuccess: refresh,
  });
}

export function useSaveNote() {
  const refresh = useRefreshDay();
  return useMutation({
    mutationFn: (v: { id: number; body: string }) =>
      api.put<{ ok: true; note: string }>(`/api/staff/appointments/${v.id}/note`, { body: v.body }),
    onSuccess: refresh,
  });
}

/** Проверка перед сохранением: что из будущих записей выпадет из новой сетки. */
export function useValidateSchedule() {
  return useMutation({
    mutationFn: (rules: WorkRule[]) => api.post<{ conflicts: Conflict[] }>('/api/staff/schedule/validate', { rules }),
  });
}

export function useSaveSchedule() {
  const refresh = useRefreshDay();
  return useMutation({
    mutationFn: (body: { rules: WorkRule[]; bufferMin?: number; horizonDays?: number; minLeadHours?: number }) =>
      api.post<Schedule>('/api/staff/schedule', body),
    onSuccess: refresh,
  });
}

export function useAddException() {
  const refresh = useRefreshDay();
  return useMutation({
    mutationFn: (body: { date: string; kind: 'off' | 'extra'; start_min: number | null; end_min: number | null; comment: string }) =>
      api.post<{ id: number }>('/api/staff/exceptions', body),
    onSuccess: refresh,
  });
}

export function useDeleteException() {
  const refresh = useRefreshDay();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ ok: true }>(`/api/staff/exceptions/${id}`),
    onSuccess: refresh,
  });
}

export function usePreviewSeries() {
  return useMutation({
    mutationFn: (body: { childId: number; serviceId: number; weekdays: number[]; timeMin: number; endsOn: string }) =>
      api.post<SeriesPreview>('/api/staff/series/preview', body),
  });
}

export function useCreateSeries() {
  const refresh = useRefreshDay();
  return useMutation({
    mutationFn: (body: { childId: number; serviceId: number; weekdays: number[]; timeMin: number; endsOn: string }) =>
      api.post<Series>('/api/staff/series', body),
    onSuccess: refresh,
  });
}

export function useStopSeries() {
  const refresh = useRefreshDay();
  return useMutation({
    mutationFn: (id: number) => api.delete<unknown>(`/api/staff/series/${id}`),
    onSuccess: refresh,
  });
}

export function useReplyReferral() {
  const refresh = useRefreshDay();
  return useMutation({
    mutationFn: (v: { id: number; reply: 'accepted' | 'declined' }) =>
      api.post<unknown>(`/api/staff/referrals/${v.id}/reply`, { reply: v.reply }),
    onSuccess: refresh,
  });
}
